import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoodIcon from '@mui/icons-material/Mood';
import { ModalIndices } from '../../../common/i-doc/i-doc.registry.ts';
import { toLocalIso } from '@/utils/datetime';
import { SchedulingBlock } from './SchedulingBlock';
import { eventColors } from '@/styles/tokens';

const ACCENT      = eventColors.feeling.main;
const ACCENT_HOVER = eventColors.feeling.dark;
import { CalendarEventDto } from '@/types/calendar';

interface AddFeelingEventModalV2Props {
  open: boolean;
  onClose: () => void;
  onSave: (data: { title: string; scheduledLdt?: string; isCompleted: boolean }) => Promise<void>;
  eventToEdit?: CalendarEventDto | null;
}

export const AddFeelingEventModalV2: React.FC<AddFeelingEventModalV2Props> = ({
  open,
  onClose,
  onSave,
  eventToEdit,
}) => {
  const isEditMode = !!eventToEdit;
  const [title, setTitle] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [isCompleted, setIsCompleted] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (eventToEdit) {
      setTitle(eventToEdit.title ?? '');
      if (eventToEdit.scheduledLdt) {
        setScheduledAt(new Date(eventToEdit.scheduledLdt));
        setShowDateTimePicker(true);
      }
      setIsCompleted(eventToEdit.isCompleted ?? false);
    } else {
      setTitle('');
      setShowDateTimePicker(false);
      setScheduledAt(null);
      setIsCompleted(false);
    }
  }, [open, eventToEdit]);

  const handleClose = () => {
    setTitle('');
    setShowDateTimePicker(false);
    setScheduledAt(null);
    setIsCompleted(false);
    onClose();
  };

  const handleSave = async () => {
    if (!title.trim()) return;
    setSaving(true);
    try {
      const scheduledLdt = showDateTimePicker && scheduledAt ? toLocalIso(scheduledAt) : undefined;
      const finalIsCompleted = showDateTimePicker ? isCompleted : true;

      await onSave({
        title: title.trim(),
        scheduledLdt,
        isCompleted: finalIsCompleted,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: '16px' } }} data-i-doc={ModalIndices.ADD_FEELING_EVENT}>
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoodIcon sx={{ color: ACCENT }} />
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{isEditMode ? 'Редагувати думки' : 'Додати думки'}</Typography>
        </Box>
        <IconButton onClick={handleClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: 'action.hover', display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          autoFocus
          fullWidth
          multiline
          rows={5}
          placeholder="Що у вас на думці?"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={saving}
          sx={{ bgcolor: 'background.paper', '& .MuiInputBase-root': { borderRadius: '12px' } }}
        />
        <SchedulingBlock
          showDateTimePicker={showDateTimePicker}
          onShowDateTimePickerChange={setShowDateTimePicker}
          scheduledAt={scheduledAt}
          onScheduledAtChange={setScheduledAt}
          isCompleted={isCompleted}
          onIsCompletedChange={setIsCompleted}
          switchLabel="Встановити дату та час"
          switchSize="small"
        />
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'action.hover' }}>
        <Button onClick={handleClose} color="inherit" disabled={saving}>Скасувати</Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!title.trim() || saving}
          sx={{ bgcolor: ACCENT, borderRadius: '10px', px: 4, '&:hover': { bgcolor: ACCENT_HOVER } }}
        >
          {isEditMode ? 'Зберегти' : 'Додати'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
